<template>
  <div>
    <v-card v-if="canEdit">
      <v-card-actions>
        <v-row>
          <v-col class="text-right">
            <v-btn
              color="primary"
              x-large
              :loading="loading"
              :disabled="loading"
              @click="save"
            >
              Salvar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>

    <!-- Auditoria Automática -->
    <v-card class="mt-6">
      <v-card-title>
        Auditoria Automática
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-switch
              v-model="preferences.auto_audit_enabled"
              color="primary"
              inset
              label="Ativar auditoria automática"
              :disabled="!canEdit"
            ></v-switch>
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="preferences.auto_audit_time"
              type="time"
              outlined
              label="Horário da Auditoria"
              :disabled="!canEdit || !preferences.auto_audit_enabled"
              :error="autoAuditErrors.time"
              :error-messages="autoAuditErrors.timeMessage"
              hint="Ex: 06:00"
              persistent-hint
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Bloqueio por Auditoria -->
    <v-card class="mt-6">
      <v-card-title>
        Bloqueio do Sistema por Auditoria
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-switch
              v-model="preferences.block_on_missing_audit"
              color="primary"
              inset
              label="Bloquear operações se a auditoria não foi realizada"
              :disabled="!canEdit"
            ></v-switch>
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="preferences.block_after_time"
              type="time"
              outlined
              label="Horário limite"
              :disabled="!canEdit || !preferences.block_on_missing_audit"
              :error="blockAuditErrors.time"
              :error-messages="blockAuditErrors.timeMessage"
              hint="Após esse horário, nenhuma operação de gravação é permitida sem auditoria"
              persistent-hint
            ></v-text-field>
          </v-col>
        </v-row>
        <v-alert
          dense
          outlined
          type="warning"
          class="mt-2"
        >
          Após o horário configurado, reservas, check-ins e qualquer operação de gravação
          serão bloqueadas até que a auditoria seja executada.
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Configurações de No-Show -->
    <v-card class="mt-6">
      <v-card-title>
        Processamento de No-Show
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <v-switch
              v-model="preferences.process_noshow_on_audit"
              color="primary"
              inset
              label="Processar No-Show automaticamente na auditoria"
              :disabled="!canEdit"
            ></v-switch>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Envio de Relatório para Sócios -->
    <v-card class="mt-6">
      <v-card-title>
        Envio de Relatório para Sócios
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <v-checkbox
              v-model="preferences.send_via_whatsapp"
              label="Enviar via WhatsApp"
              :disabled="!canEdit"
            ></v-checkbox>
          </v-col>
          <v-col cols="6">
            <v-checkbox
              v-model="preferences.send_via_email"
              label="Enviar via E-mail"
              :disabled="!canEdit"
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Configuração WhatsApp para Auditoria -->
    <v-card
      v-if="preferences.send_via_whatsapp"
      class="mt-6"
    >
      <v-card-title>
        Configuração do WhatsApp (Contexto Auditoria)
      </v-card-title>
      <v-card-text>
        <v-alert
          dense
          outlined
          type="info"
          class="mb-4"
        >
          Esta configuração é específica para envio de relatórios de auditoria.
          A configuração de vouchers de reserva está em <strong>Sistema > Configurações</strong>.
        </v-alert>
        <v-row>
          <v-col cols="12">
            <v-select
              v-model="preferences.whats_provider"
              :items="whatsProviders"
              item-text="text"
              item-value="value"
              outlined
              label="Provedor WhatsApp"
              :disabled="!canEdit"
              hint="Selecione qual API de WhatsApp utilizar para relatórios de auditoria"
              persistent-hint
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="preferences.whats_endpoint"
              outlined
              label="URL da API"
              :disabled="!canEdit"
              :error="whatsAppErrors.endpoint"
              :error-messages="whatsAppErrors.endpointMessage"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="preferences.whats_instance"
              outlined
              label="Instância"
              :disabled="!canEdit"
              :error="whatsAppErrors.instance"
              :error-messages="whatsAppErrors.instanceMessage"
              :hint="preferences.whats_provider === 'evolution' ? 'Obrigatório para Evolution API' : 'Opcional para Wuzapi'"
              persistent-hint
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="preferences.whats_key"
              type="password"
              outlined
              label="API KEY"
              :disabled="!canEdit"
              :error="whatsAppErrors.key"
              :error-messages="whatsAppErrors.keyMessage"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="preferences.whats_greeting"
              rows="3"
              outlined
              label="Mensagem de saudação"
              :disabled="!canEdit"
              hint="Mensagem que será enviada antes do relatório de auditoria"
              persistent-hint
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            md="8"
          >
            <v-alert
              :type="whatsAppStatus.connected ? 'success' : 'error'"
              dense
              text
            >
              <v-icon left>
                {{ whatsAppStatus.connected ? 'mdi-check-circle' : 'mdi-alert-circle' }}
              </v-icon>
              <strong>Status:</strong> {{ whatsAppStatus.connected ? 'Conectado' : 'Desconectado' }}
            </v-alert>
          </v-col>
          <v-col
            cols="12"
            md="4"
            class="d-flex align-center justify-center"
          >
            <v-btn
              v-if="!whatsAppStatus.connected"
              color="primary"
              :loading="loadingWhatsApp"
              :disabled="!preferences.whats_endpoint || !preferences.whats_key"
              @click="handleConnectWhatsApp"
            >
              Conectar
            </v-btn>
            <v-btn
              v-else
              color="error"
              :loading="loadingWhatsApp"
              @click="handleDisconnectWhatsApp"
            >
              Desconectar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Lista de Contatos de Sócios -->
    <v-card
      v-if="showContactsList"
      class="mt-6"
    >
      <v-card-title>
        Contatos dos Sócios
        <v-spacer></v-spacer>
        <v-btn
          v-if="canManageContacts"
          color="primary"
          @click="openContactDialog()"
        >
          <v-icon left>
            {{ icons.mdiPlus }}
          </v-icon>
          Adicionar Contato
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="contactHeaders"
          :items="contacts"
          :items-per-page="10"
          class="elevation-1"
        >
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center">
              <v-tooltip
                v-if="canManageContacts"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    class="mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click="openContactDialog(item)"
                  >
                    <v-icon small>
                      {{ icons.mdiPencilOutline }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>Editar contato</span>
              </v-tooltip>
              <v-tooltip
                v-if="canManageContacts"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="error"
                    v-bind="attrs"
                    v-on="on"
                    @click="deleteContact(item)"
                  >
                    <v-icon small>
                      {{ icons.mdiDeleteOutline }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>Excluir contato</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog para adicionar/editar contato -->
    <v-dialog
      v-model="contactDialog"
      max-width="600px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editingContact ? 'Editar Contato' : 'Novo Contato' }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="contactForm.name"
                  label="Nome*"
                  outlined
                  :error="contactFormErrors.name"
                  :error-messages="contactFormErrors.nameMessage"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="contactForm.email"
                  label="E-mail"
                  outlined
                  type="email"
                  :error="contactFormErrors.email"
                  :error-messages="contactFormErrors.emailMessage"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="contactForm.phone"
                  v-mask="whatsAppMask"
                  label="WhatsApp"
                  outlined
                  :error="contactFormErrors.phone"
                  :error-messages="contactFormErrors.phoneMessage"
                  hint="Formato: (XX)XXXXX-XXXX ou +XXXXXXXXXXX para internacional"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
          <small>*Campo obrigatório</small>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeContactDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="loadingContact"
            @click="saveContact"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmação de exclusão -->
    <v-dialog
      v-model="deleteDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title class="text-h5">
          Confirmar Exclusão
        </v-card-title>
        <v-card-text>
          Tem certeza que deseja excluir o contato <strong>{{ contactToDelete && contactToDelete.name }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="deleteDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            :loading="loadingDelete"
            @click="confirmDelete"
          >
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal QR Code -->
    <v-dialog
      v-model="showQrDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title class="justify-center">
          QR Code - Auditoria WhatsApp
        </v-card-title>
        <v-card-text class="d-flex flex-column align-center">
          <v-img
            v-if="whatsAppStatus.qr"
            :src="whatsAppStatus.qr"
            max-width="250"
            class="mx-auto my-4"
          />
          <div
            v-else
            class="text-caption text-grey"
          >
            QR code não disponível
          </div>
          <v-alert
            dense
            outlined
            type="info"
            class="mt-4"
          >
            Escaneie o QR Code com o WhatsApp do dispositivo que será usado para enviar os relatórios de auditoria.
          </v-alert>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            text
            :loading="loadingWhatsApp"
            @click="refreshQr"
          >
            Atualizar
          </v-btn>
          <v-btn
            text
            @click="showQrDialog = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import {
  mdiPlus,
  mdiPencilOutline,
  mdiDeleteOutline,
} from '@mdi/js'

export default {
  data: () => ({
    loading: false,
    loadingContact: false,
    loadingDelete: false,
    loadingWhatsApp: false,
    showQrDialog: false,
    preferences: {
      auto_audit_enabled: false,
      auto_audit_time: null,
      process_noshow_on_audit: false,
      send_via_whatsapp: false,
      send_via_email: false,
      block_on_missing_audit: false,
      block_after_time: null,
      whats_endpoint: '',
      whats_key: '',
      whats_instance: '',
      whats_provider: 'evolution',
      whats_greeting: '',
    },
    whatsAppStatus: {
      connected: false,
      qr: null,
    },
    whatsProviders: [
      { text: 'Evolution API', value: 'evolution' },
      { text: 'Wuzapi', value: 'wuzapi' },
    ],
    autoAuditErrors: {
      time: false,
      timeMessage: '',
    },
    blockAuditErrors: {
      time: false,
      timeMessage: '',
    },
    whatsAppErrors: {
      endpoint: false,
      endpointMessage: '',
      key: false,
      keyMessage: '',
      instance: false,
      instanceMessage: '',
    },
    contacts: [],
    contactDialog: false,
    deleteDialog: false,
    editingContact: null,
    contactToDelete: null,
    contactForm: {
      name: '',
      email: '',
      phone: '',
    },
    contactFormErrors: {
      name: false,
      nameMessage: '',
      email: false,
      emailMessage: '',
      phone: false,
      phoneMessage: '',
    },
    contactHeaders: [
      { text: 'Nome', value: 'name' },
      { text: 'E-mail', value: 'email' },
      { text: 'WhatsApp', value: 'phone' },
      {
        text: 'Ações', value: 'actions', sortable: false, align: 'center',
      },
    ],
    permissions: JSON.parse(localStorage.getItem('permissions')) || [],
    icons: {
      mdiPlus,
      mdiPencilOutline,
      mdiDeleteOutline,
    },
    whatsAppPollingInterval: null,
  }),
  computed: {
    showContactsList() {
      return this.preferences.send_via_whatsapp || this.preferences.send_via_email
    },
    whatsAppMask() {
      const val = this.contactForm.phone || ''
      if (val.startsWith('+')) return '+###############' // internacional

      return '(##)#####-####'
    },
    canEdit() {
      return this.hasPermission('system.audit_preferences', 'store')
    },
    canManageContacts() {
      return this.hasPermission('system.audit_preferences', 'manageContacts')
    },
  },
  created() {
    this.loadData()
  },
  beforeDestroy() {
    // Limpa o interval de polling ao destruir o componente
    if (this.whatsAppPollingInterval) {
      clearInterval(this.whatsAppPollingInterval)
    }
  },
  methods: {
    ...mapActions('auditPreferences', [
      'loadPreferences',
      'savePreferences',
      'storeContact',
      'updateContact',
      'destroyContact',
      'checkWhatsAppStatus',
      'connectWhatsApp',
      'disconnectWhatsApp',
      'reconnectWhatsApp',
    ]),

    hasPermission(dir, action) {
      if (!dir || !action) return true

      return this.permissions.some(p => p.dir === dir && p.action === action)
    },

    async loadData() {
      try {
        const response = await this.loadPreferences()
        if (response.data.preferences) {
          this.preferences = {
            auto_audit_enabled: response.data.preferences.auto_audit_enabled || false,
            auto_audit_time: response.data.preferences.auto_audit_time || null,
            process_noshow_on_audit: response.data.preferences.process_noshow_on_audit || false,
            send_via_whatsapp: response.data.preferences.send_via_whatsapp || false,
            send_via_email: response.data.preferences.send_via_email || false,
            block_on_missing_audit: response.data.preferences.block_on_missing_audit || false,
            block_after_time: response.data.preferences.block_after_time || null,
            whats_endpoint: response.data.preferences.whats_endpoint || '',
            whats_key: response.data.preferences.whats_key || '',
            whats_instance: response.data.preferences.whats_instance || '',
            whats_provider: response.data.preferences.whats_provider || 'evolution',
            whats_greeting: response.data.preferences.whats_greeting || '',
          }
        }
        if (response.data.contacts) {
          this.contacts = response.data.contacts
        }

        // Carrega status do WhatsApp se estiver habilitado
        if (this.preferences.send_via_whatsapp && this.preferences.whats_endpoint) {
          await this.verifyWhatsAppStatus()
        }
      } catch (error) {
        console.error('Erro ao carregar preferências:', error)
      }
    },

    async save() {
      if (!this.validate()) {
        return
      }

      this.loading = true

      try {
        // Prepara os dados para enviar
        const dataToSave = { ...this.preferences }

        // Se auditoria automática estiver desativada, limpa o horário
        if (!dataToSave.auto_audit_enabled) {
          dataToSave.auto_audit_time = null
        }
        if (!dataToSave.block_on_missing_audit) {
          dataToSave.block_after_time = null
        }

        await this.savePreferences(dataToSave)
        this.$toast.success('Preferências salvas com sucesso')
      } catch (error) {
        this.$toast.error('Erro ao salvar preferências')
        console.error('Erro:', error)
      } finally {
        this.loading = false
      }
    },

    validate() {
      this.autoAuditErrors.time = false
      this.autoAuditErrors.timeMessage = ''
      this.blockAuditErrors.time = false
      this.blockAuditErrors.timeMessage = ''
      this.whatsAppErrors = {
        endpoint: false,
        endpointMessage: '',
        key: false,
        keyMessage: '',
        instance: false,
        instanceMessage: '',
      }

      if (this.preferences.auto_audit_enabled && !this.preferences.auto_audit_time) {
        this.autoAuditErrors.time = true
        this.autoAuditErrors.timeMessage = 'Horário é obrigatório quando auditoria automática está ativada'
        this.$toast.error('Por favor, preencha o horário da auditoria automática')

        return false
      }

      if (this.preferences.block_on_missing_audit && !this.preferences.block_after_time) {
        this.blockAuditErrors.time = true
        this.blockAuditErrors.timeMessage = 'Defina um horário para iniciar o bloqueio'
        this.$toast.error('Defina o horário limite para o bloqueio por auditoria')

        return false
      }

      // Validação de WhatsApp
      if (this.preferences.send_via_whatsapp) {
        let valid = true

        if (!this.preferences.whats_endpoint) {
          this.whatsAppErrors.endpoint = true
          this.whatsAppErrors.endpointMessage = 'URL da API é obrigatória'
          valid = false
        }

        if (!this.preferences.whats_key) {
          this.whatsAppErrors.key = true
          this.whatsAppErrors.keyMessage = 'API Key é obrigatória'
          valid = false
        }

        if (this.preferences.whats_provider === 'evolution' && !this.preferences.whats_instance) {
          this.whatsAppErrors.instance = true
          this.whatsAppErrors.instanceMessage = 'Instância é obrigatória para Evolution API'
          valid = false
        }

        if (!valid) {
          this.$toast.error('Preencha as configurações do WhatsApp para auditoria')

          return false
        }
      }

      return true
    },

    openContactDialog(contact = null) {
      if (contact) {
        this.editingContact = contact
        this.contactForm = {
          name: contact.name,
          email: contact.email || '',
          phone: contact.phone || '',
        }
      } else {
        this.editingContact = null
        this.contactForm = {
          name: '',
          email: '',
          phone: '',
        }
      }
      this.resetContactFormErrors()
      this.contactDialog = true
    },

    closeContactDialog() {
      this.contactDialog = false
      this.editingContact = null
      this.contactForm = {
        name: '',
        email: '',
        phone: '',
      }
      this.resetContactFormErrors()
    },

    resetContactFormErrors() {
      this.contactFormErrors = {
        name: false,
        nameMessage: '',
        email: false,
        emailMessage: '',
        phone: false,
        phoneMessage: '',
      }
    },

    validateContactForm() {
      this.resetContactFormErrors()
      let valid = true

      if (!this.contactForm.name || this.contactForm.name.trim() === '') {
        this.contactFormErrors.name = true
        this.contactFormErrors.nameMessage = 'Nome é obrigatório'
        valid = false
      }

      if (this.preferences.send_via_email && (!this.contactForm.email || this.contactForm.email.trim() === '')) {
        this.contactFormErrors.email = true
        this.contactFormErrors.emailMessage = 'E-mail é obrigatório quando envio por e-mail está ativo'
        valid = false
      }

      if (this.contactForm.email && this.contactForm.email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(this.contactForm.email)) {
          this.contactFormErrors.email = true
          this.contactFormErrors.emailMessage = 'E-mail inválido'
          valid = false
        }
      }

      if (this.preferences.send_via_whatsapp && (!this.contactForm.phone || this.contactForm.phone.trim() === '')) {
        this.contactFormErrors.phone = true
        this.contactFormErrors.phoneMessage = 'WhatsApp é obrigatório quando envio por WhatsApp está ativo'
        valid = false
      }

      return valid
    },

    async saveContact() {
      if (!this.validateContactForm()) {
        return
      }

      this.loadingContact = true

      try {
        if (this.editingContact) {
          const response = await this.updateContact({
            id: this.editingContact.id,
            ...this.contactForm,
          })
          const index = this.contacts.findIndex(c => c.id === this.editingContact.id)
          if (index !== -1) {
            this.$set(this.contacts, index, response.data)
          }
          this.$toast.success('Contato atualizado com sucesso')
        } else {
          const response = await this.storeContact(this.contactForm)
          this.contacts.push(response.data)
          this.$toast.success('Contato adicionado com sucesso')
        }
        this.closeContactDialog()
      } catch (error) {
        this.$toast.error('Erro ao salvar contato')
        console.error('Erro:', error)
      } finally {
        this.loadingContact = false
      }
    },

    deleteContact(contact) {
      this.contactToDelete = contact
      this.deleteDialog = true
    },

    async confirmDelete() {
      this.loadingDelete = true

      try {
        await this.destroyContact(this.contactToDelete.id)
        const index = this.contacts.indexOf(this.contactToDelete)
        this.contacts.splice(index, 1)
        this.$toast.success('Contato excluído com sucesso')
        this.deleteDialog = false
        this.contactToDelete = null
      } catch (error) {
        this.$toast.error('Erro ao excluir contato')
        console.error('Erro:', error)
      } finally {
        this.loadingDelete = false
      }
    },

    async verifyWhatsAppStatus() {
      try {
        const response = await this.checkWhatsAppStatus()
        const state = response.data?.instance?.state || 'close'
        this.whatsAppStatus.connected = state === 'open'
      } catch (error) {
        console.error('Erro ao verificar status do WhatsApp:', error)
        this.whatsAppStatus.connected = false
      }
    },

    async handleConnectWhatsApp() {
      this.loadingWhatsApp = true
      try {
        const response = await this.connectWhatsApp()

        if (response.data.base64) {
          this.whatsAppStatus.qr = response.data.base64
          this.showQrDialog = true

          // Limpa interval anterior se existir
          if (this.whatsAppPollingInterval) {
            clearInterval(this.whatsAppPollingInterval)
          }

          // Polling para verificar se conectou
          this.whatsAppPollingInterval = setInterval(async () => {
            await this.verifyWhatsAppStatus()
            if (this.whatsAppStatus.connected) {
              clearInterval(this.whatsAppPollingInterval)
              this.whatsAppPollingInterval = null
              this.showQrDialog = false
              this.$toast.success('WhatsApp conectado com sucesso!')
            }
          }, 5000)

          // Para o polling após 2 minutos
          setTimeout(() => {
            if (this.whatsAppPollingInterval) {
              clearInterval(this.whatsAppPollingInterval)
              this.whatsAppPollingInterval = null
              if (!this.whatsAppStatus.connected) {
                this.$toast.warning('Tempo limite para conectar expirado. Tente novamente.')
              }
            }
          }, 120000)
        } else if (response.data.already_logged_in) {
          this.$toast.info('WhatsApp já está conectado')
          this.whatsAppStatus.connected = true
        } else if (response.data.success === false) {
          this.$toast.error(response.data.message || 'Erro ao conectar WhatsApp')
        }
      } catch (error) {
        this.$toast.error('Erro ao conectar WhatsApp')
        console.error('Erro:', error)
      } finally {
        this.loadingWhatsApp = false
      }
    },

    async handleDisconnectWhatsApp() {
      this.loadingWhatsApp = true
      try {
        await this.disconnectWhatsApp()
        this.whatsAppStatus.connected = false
        this.$toast.success('WhatsApp desconectado')
      } catch (error) {
        this.$toast.error('Erro ao desconectar WhatsApp')
        console.error('Erro:', error)
      } finally {
        this.loadingWhatsApp = false
      }
    },

    async refreshQr() {
      this.loadingWhatsApp = true
      try {
        const response = await this.reconnectWhatsApp()
        if (response.data.base64) {
          this.whatsAppStatus.qr = response.data.base64
          this.$toast.success('QR Code atualizado')
        } else {
          this.$toast.warning('QR Code não disponível')
        }
      } catch (error) {
        this.$toast.error('Erro ao atualizar QR Code')
        console.error('Erro:', error)
      } finally {
        this.loadingWhatsApp = false
      }
    },
  },
}
</script>
